import React, { useEffect, useMemo, useState } from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import { global } from "@shared/libs/Global";
import { formatNumber, toNum } from "@shared/utils/CommonUtils";
import SnChart from "@shared/components/Grid/SnChart";
import { icoPrd01 } from "./icons";

interface Props {
  cdWidget?: string;
  nmWidget?: string;
  searchData?: any;
  refreshKey?: number;
}

const SnSysWidget009: React.FC<Props> = ({
  nmWidget,
  searchData,
  refreshKey,
}) => {
  const { userInfo } = global();
  const [summary, setSummary] = useState<any>({});
  const [trendRows, setTrendRows] = useState<any[]>([]);

  const params = useMemo(
    () => ({
      cd_firm: searchData?.cd_firm || userInfo.cd_firm,
      dt: searchData?.dt,
    }),
    [searchData?.cd_firm, searchData?.dt, userInfo.cd_firm]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data: any = await fnGetDataSet({
          id: "sn_sys_widget_009_01",
          params,
        });

        setSummary(data?.[0]?.[0] || {});
        setTrendRows(data?.[1] || []);
      } catch (error) {
        setSummary({});
        setTrendRows([]);
      }
    };

    fetchData();
  }, [params, refreshKey]);

  const summaryRows = [
    {
      label: "생산실적량",
      value: `${formatNumber(toNum(summary?.qt), { fractionDigits: 0 })} 개`,
    },
    {
      label: "양품실적량",
      value: `${formatNumber(toNum(summary?.qt_w), { fractionDigits: 0 })} 개`,
    },
    {
      label: "불량실적량",
      value: `${formatNumber(toNum(summary?.qt_b), { fractionDigits: 0 })} 개`,
    },
  ];

  const chartData = useMemo(
    () =>
      trendRows.map((row) => ({
        ...row,
        cd_day: row?.cd_day || "",
        dt_run: row?.dt_run || row?.cd_day || "",
        qt: toNum(row?.qt),
      })),
    [trendRows]
  );

  return (
    <div className="flex h-full min-h-[280px] w-full flex-col rounded-lg border border-gray-100 bg-white px-4 py-3 shadow-sm">
      <div className="mb-4 flex items-center gap-3">
        <div className="aspect-square h-10 shrink-0">
          <img src={icoPrd01} alt="" className="h-full w-full object-contain" />
        </div>
        <div className="truncate text-xl font-bold text-slate-900">{nmWidget}</div>
      </div>

      <div className="flex flex-col gap-1.5">
        {summaryRows.map((row) => (
          <div
            key={row.label}
            className="flex items-baseline justify-between gap-4 text-base font-semibold"
          >
            <span className="shrink-0 text-slate-800">{row.label}</span>
            <span className="min-w-0 whitespace-nowrap text-right text-slate-900">
              {row.value}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-4 text-sm font-semibold text-slate-800">
        생산량추이
      </div>
      <div className="min-h-0 flex-1 overflow-visible pb-2">
        <SnChart
          type="custom"
          data={chartData}
          height="100%"
          width="100%"
          xKey="cd_day"
          yKey="qt"
          seriesOptions={{
            series: [
              {
                type: "bar",
                direction: "vertical",
                xKey: "cd_day",
                yKey: "qt",
                yName: "생산량",
                fill: "#10b981",
                stroke: "#059669",
                tooltip: {
                  renderer: ({ datum, yKey }: any) => ({
                    title: datum?.dt_run || datum?.cd_day || "",
                    content: `${formatNumber(toNum(datum?.[yKey]), {
                      fractionDigits: 0,
                    })} EA`,
                  }),
                },
              },
            ],
          }}
          options={{
            padding: { top: 6, right: 10, bottom: 24, left: 6 },
            legend: { enabled: false },
            axes: [
              {
                type: "category",
                position: "bottom",
                label: { fontSize: 12 },
              },
              {
                type: "number",
                position: "left",
                label: { enabled: false },
                nice: true,
              },
            ],
          }}
        />
      </div>
    </div>
  );
};

export default SnSysWidget009;
